import { Controller } from "@hotwired/stimulus";

/**
 * 車両の概算見積及び購入申請
 *   price: 計算する対象の金額(typeがcheckboxの場合はチェック時に計算に含まれる)
 *   amount: リクエスト送信する合計金額
 *   amountPlaceholder: 表示する合計金額
 *   aioiWarrantyYear: 修理保証のcheckbox
 *   warrantyAgreements: 修理保証ガイドのセクション
 *   returnGuaranteeAgreements: 返品補償ガイドのセクション
 *   confirmPurchase: 購入申請のsubmit
 *   warrantyAgreed: 修理保証の同意checkbox
 *   returnGuaranteeAgreed: 返品補償の同意checkbox
 *   returnGuaranteeOption: 返品補償オプションのcheckbox
 */
export default class extends Controller {
  static targets = [
    "price",
    "amount",
    "amountPlaceholder",
    "aioiWarrantyYear",
    "warrantyAgreements",
    "warrantyAndReturnGuaranteeAgreements",
    "confirmPurchase",
    "warrantyAgreed",
    "returnGuaranteeAgreed",
    "returnGuaranteeOption",
    "confirmationModal",
    "warrantySection",
    "returnGuaranteeSection",
    "warrantyBottomSpacer",
    "finalConfirmation",
  ];

  connect() {
    this.preventSubmitLocked = false;
    this.setActiveWarrantyAgreement();
    this.sumAmount();
    this.#setupModalContentController();
  }

  disconnect() {
    this.#cleanupModalContentController();
  }

  /**
   * 金額合計を計算する
   */
  sumAmount() {
    const prices = this.priceTargets.map((price) => {
      if (price.type === "checkbox") {
        return price.checked ? Number(price.dataset.price) : 0;
      } else {
        return Number(price.dataset.price);
      }
    });
    const sum = prices.reduce((sum, price) => sum + price);

    this.amountPlaceholderTargets.forEach((amountPlaceholder) => {
      if (isNaN(sum)) {
        amountPlaceholder.textContent = "---";
      } else {
        amountPlaceholder.textContent = sum.toLocaleString();
      }
    });

    this.amountTarget.value = sum;
  }

  /**
   * 修理保証のオプション設定時の保証ガイド表示
   */
  setActiveWarrantyAgreement() {
    // モーダルコンテンツの表示制御
    this.#determineModalFlow();
  }

  /**
   * ボタンクリック時に一時的に無効化する
   * disable-with だとPDF開いた時にリダイレクト等で制御できないので自前で無効にして戻すようにした
   * @param {PointerEvent} event
   */
  preventSubmitOnClick(event) {
    if (this.preventSubmitLocked) {
      return event.preventDefault();
    }

    this.preventSubmitLocked = true;
    const target = $(event.currentTarget);
    const originalHtml = target.html();
    target.text("送信中...");

    setTimeout(() => {
      this.preventSubmitLocked = false;
      target.html(originalHtml);
    }, 2000);
  }

  /**
   * change-modal-contentコントローラーの設定
   */
  #setupModalContentController() {
    if (!this.hasConfirmationModalTarget) return;

    // モーダルが開かれた時のイベントリスナー
    $(this.confirmationModalTarget).on(
      "show.bs.modal",
      this.#determineModalFlow,
    );
  }

  /**
   * モーダルのイベントリスナーをクリーンアップ
   */
  #cleanupModalContentController() {
    if (!this.hasConfirmationModalTarget) return;

    // イベントリスナーを削除する
    $(this.confirmationModalTarget).off(
      "show.bs.modal",
      this.#determineModalFlow,
    );
  }

  /**
   * 選択されたオプションに基づいてモーダルの表示順序を決定
   */
  #determineModalFlow = () => {
    const hasWarranty =
      this.hasAioiWarrantyYearTarget && this.aioiWarrantyYearTarget.checked;
    const hasReturnGuarantee = this.#checkReturnGuaranteeSelected();

    if (!this.hasConfirmationModalTarget) {
      // モーダルがなくてもボタン状態は更新する
      this.#updateConfirmButtonState();
      return;
    }

    // change-modal-contentコントローラーを取得
    const modalController =
      this.application.getControllerForElementAndIdentifier(
        this.confirmationModalTarget,
        "change-modal-content",
      );

    if (!modalController) {
      // コントローラーが取得できなくてもボタン状態は更新する
      this.#updateConfirmButtonState();
      return;
    }

    // 購入申請ボタンの制御
    if (hasWarranty && hasReturnGuarantee) {
      // 両方選択されている場合は統合画面を表示（通常のpurchase_orders用）
      if (this.hasWarrantyAndReturnGuaranteeAgreementsTarget) {
        modalController.showModalContentByNameString(
          "warranty-and-return-guarantee-agreement",
        );
        // セクションの表示/非表示を設定
        this.#setupAgreementSections(hasWarranty, hasReturnGuarantee);
      }
    } else if (hasWarranty) {
      // 修理保証のみの場合
      if (this.hasWarrantyAndReturnGuaranteeAgreementsTarget) {
        // 統合画面がある場合（通常のpurchase_orders）
        modalController.showModalContentByNameString(
          "warranty-and-return-guarantee-agreement",
        );
        this.#setupAgreementSections(hasWarranty, false);
      } else {
        // 統合画面がない場合（TGR TRADE）
        modalController.showModalContentByNameString("warranty-agreement");
      }
    } else if (hasReturnGuarantee) {
      // 返品補償のみの場合（通常のpurchase_ordersのみ）
      if (this.hasWarrantyAndReturnGuaranteeAgreementsTarget) {
        modalController.showModalContentByNameString(
          "warranty-and-return-guarantee-agreement",
        );
        this.#setupAgreementSections(false, hasReturnGuarantee);
      }
    } else {
      // どちらも選択されていない場合：直接最終確認画面を表示
      modalController.showModalContentByNameString("final-confirmation");
    }

    // 同意チェックボックスのリセット
    if (hasWarranty || hasReturnGuarantee) {
      if (this.hasWarrantyAgreedTarget) {
        this.warrantyAgreedTarget.checked = false;
      }
      if (this.hasReturnGuaranteeAgreedTarget) {
        this.returnGuaranteeAgreedTarget.checked = false;
      }
    }

    // 購入申請ボタンの状態を更新
    this.#updateConfirmButtonState();
  };

  /**
   * 同意画面のセクション表示を設定
   */
  #setupAgreementSections(hasWarranty, hasReturnGuarantee) {
    // 修理保証セクション
    if (this.hasWarrantySectionTarget) {
      this.warrantySectionTarget.style.display = hasWarranty ? "block" : "none";
    }

    // 修理保証の下のスペーサー（両方選択時のみ表示）
    if (this.hasWarrantyBottomSpacerTarget) {
      this.warrantyBottomSpacerTarget.style.display =
        hasWarranty && hasReturnGuarantee ? "block" : "none";
    }

    // 返品補償セクション（両方選択時は初期非表示）
    if (this.hasReturnGuaranteeSectionTarget) {
      if (hasWarranty && hasReturnGuarantee) {
        // 両方選択されている場合は修理保証への同意状態によって表示を切り替え
        const warrantyAgreed =
          this.hasWarrantyAgreedTarget && this.warrantyAgreedTarget.checked;
        this.returnGuaranteeSectionTarget.style.display = warrantyAgreed
          ? "block"
          : "none";
      } else if (hasReturnGuarantee) {
        // 返品補償のみの場合は表示
        this.returnGuaranteeSectionTarget.style.display = "block";
      } else {
        this.returnGuaranteeSectionTarget.style.display = "none";
      }
    }
  }

  /**
   * 返品補償オプションが選択されているかチェック
   */
  #checkReturnGuaranteeSelected() {
    // returnGuaranteeOptionターゲットが存在し、チェックされているか確認
    if (this.hasReturnGuaranteeOptionTarget) {
      return this.returnGuaranteeOptionTarget.checked;
    }
    return false;
  }

  /**
   * 修理保証のチェックボックスのイベントハンドラー（TGR TRADE用シンプル版）
   */
  handleWarrantyCheckSimple() {
    // ボタン状態を更新するのみ
    this.#updateConfirmButtonState();
  }

  /**
   * 修理保証のチェックボックスのイベントハンドラー
   */
  handleWarrantyCheck(event) {
    const hasReturnGuarantee = this.#checkReturnGuaranteeSelected();
    const hasWarranty =
      this.hasAioiWarrantyYearTarget && this.aioiWarrantyYearTarget.checked;

    if (event.target.checked && hasWarranty && hasReturnGuarantee) {
      // 修理保証に同意し、返品補償も選択されている場合
      // 返品補償セクションを表示してスクロール
      this.#showReturnGuaranteeSection();
    }

    // ボタン状態を更新
    this.#updateConfirmButtonState();
  }

  /**
   * 返品補償のチェックボックスのイベントハンドラー
   */
  handleReturnGuaranteeCheck() {
    // ボタン状態を更新するのみ（コンテンツは切り替えない）
    this.#updateConfirmButtonState();
  }

  /**
   * 返品補償セクションを表示してスムーズにスクロール
   */
  #showReturnGuaranteeSection() {
    if (!this.hasReturnGuaranteeSectionTarget) return;

    // 返品補償セクションを表示
    this.returnGuaranteeSectionTarget.style.display = "block";

    // DOMの更新を待ってからスクロール
    setTimeout(() => {
      // モーダルボディを確実に取得
      let modalBody = null;

      // confirmationModalTargetがある場合はそこから取得
      if (this.hasConfirmationModalTarget) {
        modalBody = this.confirmationModalTarget.querySelector(".modal-body");
      }

      // それでも見つからない場合は、ドキュメント全体から検索
      if (!modalBody) {
        modalBody = document.querySelector(
          "#purchase-order-confirmation .modal-body",
        );
      }

      if (modalBody && this.returnGuaranteeSectionTarget) {
        // 返品補償セクションのカード要素を取得
        const returnGuaranteeCard =
          this.returnGuaranteeSectionTarget.querySelector(".card");

        // スクロール先の位置を計算
        // カード要素があればその位置、なければセクションの位置を使用
        const targetElement =
          returnGuaranteeCard || this.returnGuaranteeSectionTarget;

        // モーダルボディに対する相対位置を直接計算
        const targetOffset = targetElement.offsetTop;
        const targetScroll = targetOffset - 20; // 少し余白を持たせる

        // 標準APIでモーダル内をスムーズにスクロール
        modalBody.scrollTo({ top: targetScroll, behavior: "smooth" });
      }
    }, 200); // DOM更新のため待機時間を設定
  }

  /**
   * 購入申請ボタンのクリックハンドラー
   */
  handlePurchaseButtonClick(event) {
    event.preventDefault();

    // 最終確認画面へ遷移
    if (!this.hasConfirmationModalTarget) return;

    const modalController =
      this.application.getControllerForElementAndIdentifier(
        this.confirmationModalTarget,
        "change-modal-content",
      );

    if (modalController) {
      modalController.showModalContentByNameString("final-confirmation");
    }
  }

  /**
   * 最終確認画面から同意画面に戻る
   */
  backToAgreement(event) {
    event.preventDefault();

    if (!this.hasConfirmationModalTarget) return;

    const hasWarranty =
      this.hasAioiWarrantyYearTarget && this.aioiWarrantyYearTarget.checked;
    const hasReturnGuarantee = this.#checkReturnGuaranteeSelected();

    // 同意が必要なオプションが無い場合はモーダルを閉じる
    if (!hasWarranty && !hasReturnGuarantee) {
      $(this.confirmationModalTarget).modal("hide");
      return;
    }

    const modalController =
      this.application.getControllerForElementAndIdentifier(
        this.confirmationModalTarget,
        "change-modal-content",
      );

    if (!modalController) return;

    // 統合画面が存在する場合は選択状態に合わせて表示を調整
    if (this.hasWarrantyAndReturnGuaranteeAgreementsTarget) {
      modalController.showModalContentByNameString(
        "warranty-and-return-guarantee-agreement",
      );
      this.#setupAgreementSections(hasWarranty, hasReturnGuarantee);
    } else if (hasWarranty && this.hasWarrantyAgreementsTarget) {
      // 修理保証のみの場合は専用画面へ戻す
      modalController.showModalContentByNameString("warranty-agreement");
    }

    this.#updateConfirmButtonState();
  }

  /**
   * 購入申請ボタンの有効/無効状態を統合的に管理
   */
  #updateConfirmButtonState() {
    if (!this.hasConfirmPurchaseTarget) return;

    const hasWarranty =
      this.hasAioiWarrantyYearTarget && this.aioiWarrantyYearTarget.checked;
    const hasReturnGuarantee = this.#checkReturnGuaranteeSelected();

    let shouldBeDisabled = false;

    // 修理保証が選択されている場合、同意が必要
    if (hasWarranty) {
      shouldBeDisabled =
        !this.hasWarrantyAgreedTarget || !this.warrantyAgreedTarget.checked;
    }

    // 返品補償が選択されている場合、同意が必要
    // 修理保証の状態に関わらず、返品補償も確認する
    if (hasReturnGuarantee) {
      shouldBeDisabled =
        shouldBeDisabled ||
        !this.hasReturnGuaranteeAgreedTarget ||
        !this.returnGuaranteeAgreedTarget.checked;
    }

    this.confirmPurchaseTarget.disabled = shouldBeDisabled;
  }
}
